import {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {createDefaultTutorialEstado, isTutorialModuleKey} from '@constants/tutorialModules';
import {authService} from '@services/auth.service';
import {tutorialService} from '@services/tutorial.service';
import {getActiveTenantId} from '@utils/tenant';

const TutorialTrackerContext = createContext(null);

const hasTenantContext = () => Boolean(getActiveTenantId() ?? authService.getTenantId());

export const TutorialTrackerProvider = ({children}) => {
    const [tutoriales, setTutoriales] = useState(null);
    const [cargando, setCargando] = useState(true);

    const refrescar = useCallback(async () => {
        if (!authService.isAuthenticated() || !hasTenantContext()) {
            const estado = createDefaultTutorialEstado();
            setTutoriales(estado);
            setCargando(false);
            return estado;
        }

        setCargando(true);

        try {
            const estado = await tutorialService.getEstado();
            setTutoriales(estado);
            return estado;
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('[TutorialTracker] No se pudo cargar GET /tutoriales/estado', error);
            }
            const estado = createDefaultTutorialEstado();
            setTutoriales(estado);
            return estado;
        } finally {
            setCargando(false);
        }
    }, []);

    useEffect(() => {
        refrescar();
    }, [refrescar]);

    useEffect(() => {
        const handleAuthChange = () => {
            if (authService.isAuthenticated() && hasTenantContext()) {
                refrescar();
                return;
            }

            setTutoriales(createDefaultTutorialEstado());
            setCargando(false);
        };

        window.addEventListener('auth-session-changed', handleAuthChange);
        return () => window.removeEventListener('auth-session-changed', handleAuthChange);
    }, [refrescar]);

    const marcarComoCompletado = useCallback(async modulo => {
        if (!modulo || !isTutorialModuleKey(modulo)) {
            if (process.env.NODE_ENV === 'development') {
                console.warn(`[useTutorialTracker] Clave de tutorial desconocida: "${modulo}"`);
            }
            return;
        }

        setTutoriales(prev => ({
            ...(prev || {}),
            [modulo]: true,
        }));

        if (!hasTenantContext()) {
            return;
        }

        try {
            await tutorialService.completar(modulo);
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn(`[useTutorialTracker] No se pudo persistir "${modulo}"`, error);
            }
        }
    }, []);

    const reiniciarTutorial = useCallback(async modulo => {
        if (!modulo) {
            return;
        }

        setTutoriales(prev => ({
            ...(prev || {}),
            [modulo]: false,
        }));
    }, []);

    const estaCompletado = useCallback(
        modulo => tutoriales?.[modulo] === true,
        [tutoriales]
    );

    const value = useMemo(() => ({
        tutoriales,
        cargando,
        refrescar,
        marcarComoCompletado,
        reiniciarTutorial,
        estaCompletado,
    }), [tutoriales, cargando, refrescar, marcarComoCompletado, reiniciarTutorial, estaCompletado]);

    return (
        <TutorialTrackerContext.Provider value={value}>
            {children}
        </TutorialTrackerContext.Provider>
    );
};

export const useTutorialTrackerContext = () => {
    const context = useContext(TutorialTrackerContext);
    if (!context) {
        throw new Error('useTutorialTracker debe usarse dentro de TutorialTrackerProvider.');
    }
    return context;
};
